/* WP-10 Arrange a collection (§14.5.3 /collect/new): single scrolling
 * focused flow — boxes, where, when, note, declaration, step-up, submit.
 * F1: the submit is driven by a persisted owner-scoped intent (stable
 * idempotency key + immutable body) so retry/reload/step-up recovery can
 * never create a duplicate request, and the UI stays honest about
 * submitting / uncertain / confirmed outcomes.
 * F1.1: intent resolution is authoritative — the server-side intent marker
 * (RECEIVED → COMMITTED | REJECTED) is probed for recovery and the abandon
 * endpoint settles an unresolved intent before any replacement key exists.
 * A snapshot "not found", a throttled retry, or a failed probe can never
 * delete the original intent. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isPortalApiError, newIdempotencyKey, portalApi } from '../api/portal-api';
import type {
  HumidorResponse,
  PickupRequestDetail,
  PickupRequestIntentStatus,
  PickupShopEligibility,
  PortalPickupShop,
  SubmitPickupRequestBody,
} from '../api/types';
import { useSession } from '../auth/session-context';
import { isStepUpCancelled, useStepUp } from '../auth/step-up-context';
import { passkeySupported } from '../auth/passkey';
import {
  Banner,
  PrimaryButton,
  SectionHeader,
  StepNumber,
  TextLink,
} from '../components/primitives';
import { DateChip, ShopChoice } from '../components/tiles';
import { SuccessTickIllustration, StorefrontIllustration } from '../illustrations';
import { BackIcon } from '../icons';
import { mediaUrl, shopProfile, useAppearance } from '../appearance/appearance';
import { currentPortalLocale } from '../i18n';
import { civilDayNumber, civilDowLabel, civilWeekday, formatDate } from '../utils/dates';
import {
  ELIGIBILITY_STALE_MAX_RETRIES,
  ELIGIBILITY_STALE_RETRY_MS,
  eligibilityDateKeys,
  eligibilityStale,
  isDateEligible,
} from '../utils/pickup-eligibility';
import {
  clearDraft,
  clearIntent,
  foreignSubmittingIntent,
  getPortalOwnerScope,
  intentBodyEquals,
  loadDraft,
  loadIntent,
  loadPersistedIntent,
  markIntentUncertain,
  persistedIntentBinding,
  PortalIntentNotDurableError,
  PortalOwnerChangedError,
  saveIntent,
  touchIntentAttempt,
  type CollectIntent,
} from '../utils/selection';
import { errorText } from '../utils/error-text';

const NOTE_MAX = 300;
/** Staleness heuristic only — a RECEIVED marker this old is probably not
 * still in flight, but age never proves an attempt died. Server coordination
 * (marker probe + serialized abandon) remains the safety mechanism; this
 * window only decides which honest state the banner shows. */
const RECEIVED_STALE_MS = 2 * 60 * 1000;

type IntentPhase = 'pending' | 'processing' | 'uncertain';

type IntentProbe =
  | { kind: 'committed'; requestNumber: string }
  | { kind: 'rejected'; code?: string }
  | { kind: 'received'; stale: boolean }
  | { kind: 'absent' }
  | { kind: 'unknown' }
  | { kind: 'unauthorized' };

type SelectedBox = {
  boxRef: string;
  productName: string;
  packLabel: string;
  boxYear: number | null;
  /** Authoritative pack size from /humidor — never parsed from the name. */
  sticksInBox: number;
};

/** F2 — the eligibility state machine for the When step. `owner` records
 * which session owner produced the answer so a mid-flight account switch
 * can never render (or submit against) another owner's policy window. */
type EligibilityState =
  | { status: 'idle' }
  | { status: 'loading'; shopId: string }
  | { status: 'ready'; shopId: string; owner: string | null; data: PickupShopEligibility }
  /* F2.1 — an answer that arrived already stale (or with malformed
   * freshness metadata) is kept as 'stale', not 'ready': it can never
   * authorize a submission, and `exhausted` tells the UI whether the
   * bounded auto-retry budget for this cycle is spent. */
  | {
      status: 'stale';
      shopId: string;
      owner: string | null;
      data: PickupShopEligibility;
      exhausted: boolean;
    }
  | { status: 'error'; shopId: string; owner: string | null; message: string };

export function CollectNewPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const navigate = useNavigate();
  const { state } = useSession();
  const { requireStepUp, withStepUp } = useStepUp();

  const [boxes, setBoxes] = useState<SelectedBox[]>([]);
  const [loose, setLoose] = useState<Array<{ productId: string; name: string; sticks: number }>>(
    [],
  );
  const [shops, setShops] = useState<PortalPickupShop[]>([]);
  const [shopId, setShopId] = useState<string | null>(null);
  const [shopQuery, setShopQuery] = useState('');
  const [date, setDateState] = useState<string | null>(null);
  const [elig, setElig] = useState<EligibilityState>({ status: 'idle' });
  const [eligRetry, setEligRetry] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [phase, setPhase] = useState<IntentPhase>('pending');
  const [storageWeak, setStorageWeak] = useState(false);
  const [done, setDone] = useState<{ requestNumber: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingIntent, setPendingIntent] = useState<CollectIntent | null>(() => loadIntent());

  /* F1.3 — the FIN-01 owner-keyed remount destroys this page mid-submit, so
   * the interrupted continuation's setError never paints. The remounted
   * instance re-derives the honest notice from durable evidence: a
   * 'submitting' intent under another owner's namespace exists only when an
   * owner flip interrupted a dispatch (nothing was sent, the original
   * owner's record is preserved for their return). */
  useEffect(() => {
    if (!pendingIntent && foreignSubmittingIntent()) {
      setError(t('collect.new.ownerChanged'));
    }
  }, [pendingIntent, t]);

  const draft = useMemo(() => loadDraft(), []);

  /* F2 — date selection is a ref-mirrored state so async eligibility
   * continuations always see the latest pick (a setState updater's own
   * closure would go stale across awaits). */
  const dateRef = useRef<string | null>(null);
  const setDate = (d: string | null) => {
    dateRef.current = d;
    setDateState(d);
  };
  const eligRef = useRef(elig);
  eligRef.current = elig;
  const eligSeq = useRef(0);
  /* F2.1-A — stale-refresh cycle. `key` identifies the cycle's context
   * (owner + shop); `used` counts the automatic retries already consumed
   * by stale answers inside it. The budget resets only on a fresh usable
   * answer, a context change (key mismatch), or an explicit user retry —
   * never on a `loading` transition, which is why the earlier in-effect
   * counter could loop forever. */
  const staleCycle = useRef({ key: '', used: 0 });
  const mountedRef = useRef(true);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  useAppearance(); // re-render when the published bundle lands/changes
  /* F3-0 — the guard must be restored in SETUP, not only cleared in
   * cleanup: React.StrictMode replays effects in development
   * (setup → cleanup → setup), so a cleanup-only flag stays false after
   * the replay and every eligibility answer is discarded. The first
   * setup re-arms it; only a real unmount leaves it false. */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const ownerScope = state.status === 'authed' ? state.session.customer.ownerScope : null;
  /* The dispatch layer's owner (module scope) is the authoritative one F1
   * uses — re-read each render so a scope flip re-gates the rendered answer
   * even when the session object has not re-rendered yet. It is deliberately
   * NOT a fetch dep: the submit gate's own refetch owns the owner-change
   * path, and a dep here would double-fire and drop that awaited answer. */
  const moduleScope = getPortalOwnerScope();

  /** F2 — fetch the server's eligibility answer for one shop. Sequence +
   * owner guards drop late or cross-account responses; a kept date survives
   * only while it is still eligible (never silently substituted). Returns
   * the fresh answer so the submit gate can await it inline. */
  const fetchEligibility = async (sid: string): Promise<PickupShopEligibility | null> => {
    const seq = ++eligSeq.current;
    const owner = getPortalOwnerScope();
    setElig({ status: 'loading', shopId: sid });
    const fresh = () => seq === eligSeq.current && getPortalOwnerScope() === owner;
    try {
      const data = await portalApi.get<PickupShopEligibility>(
        `/pickup-shops/${encodeURIComponent(sid)}/eligibility`,
      );
      if (!fresh() || !mountedRef.current) return null;
      if (eligibilityStale(data)) {
        /* F2.1-A — a stale answer lands as 'stale', never 'ready', so it
         * can never authorize a submission. The cycle budget is consumed
         * HERE at classification time (keyed by owner+shop), so the
         * `loading` transition the retry causes cannot reset it — at most
         * ELIGIBILITY_STALE_MAX_RETRIES automatic refetches follow the
         * first stale answer, then the strip settles exhausted. */
        const key = `${owner ?? ''}:${sid}`;
        if (staleCycle.current.key !== key) staleCycle.current = { key, used: 0 };
        const exhausted = staleCycle.current.used >= ELIGIBILITY_STALE_MAX_RETRIES;
        if (!exhausted) staleCycle.current.used += 1;
        setElig({ status: 'stale', shopId: sid, owner, data, exhausted });
        return null;
      }
      staleCycle.current = { key: '', used: 0 };
      setElig({ status: 'ready', shopId: sid, owner, data });
      if (dateRef.current && !isDateEligible(dateRef.current, data)) {
        setDate(null);
        setNotice(t('collect.new.dateUnavailable'));
      }
      return data;
    } catch (err) {
      if (!fresh() || !mountedRef.current) return null;
      setElig({ status: 'error', shopId: sid, owner, message: errorText(err, t) });
      return null;
    }
  };

  // Fetch eligibility whenever the selected shop, owner or retry changes —
  // a late response for a previously selected shop can never overwrite the
  // current shop's answer (sequence guard).
  useEffect(() => {
    if (!shopId) {
      setElig({ status: 'idle' });
      return;
    }
    void fetchEligibility(shopId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, eligRetry, ownerScope, t]);

  /* Refresh scheduling (F2.1-A):
   * - 'ready': one refetch at the documented validUntil bound. A malformed
   *   or already-past bound can never schedule — a fresh answer can only
   *   arrive through 'stale' classification, which keeps it bounded.
   * - 'stale' with budget left: one automatic retry after
   *   ELIGIBILITY_STALE_RETRY_MS. The budget was already consumed in
   *   fetchEligibility, so the 'loading' hop this retry causes cannot
   *   reset it — the cycle ends after two automatic retries.
   * - 'stale' exhausted / error / loading / idle: nothing scheduled —
   *   the strip settles on explicit-retry feedback.
   * Cleanup runs on every dep change and unmount, so shop/owner switches
   * and navigation can never leave an obsolete timer behind. */
  useEffect(() => {
    if (elig.status === 'ready') {
      const ms = Date.parse(elig.data.validUntil) - Date.now();
      if (!Number.isFinite(ms) || ms <= 0) return;
      const id = setTimeout(() => setEligRetry((n) => n + 1), ms);
      return () => clearTimeout(id);
    }
    if (elig.status !== 'stale' || elig.exhausted) return;
    const id = setTimeout(() => setEligRetry((n) => n + 1), ELIGIBILITY_STALE_RETRY_MS);
    return () => clearTimeout(id);
  }, [elig]);

  /** F2.1-A — explicit user retry always starts a new bounded cycle. */
  const retryEligibility = () => {
    staleCycle.current = { key: '', used: 0 };
    setEligRetry((n) => n + 1);
  };

  // Returning to a page whose answer may have expired revalidates it. The
  // refetch runs through the same cycle budget, so focus can never
  // recreate an unlimited loop — on an exhausted cycle it produces at
  // most one bounded request per event.
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      const cur = eligRef.current;
      if (cur.status === 'stale' || (cur.status === 'ready' && eligibilityStale(cur.data))) {
        setEligRetry((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);

  // Load selection → resolve box details + shops.
  useEffect(() => {
    let live = true;
    Promise.all([
      portalApi.get<HumidorResponse>('/humidor'),
      portalApi.get<{ items: PortalPickupShop[] }>('/pickup-shops'),
    ])
      .then(([h, s]) => {
        if (!live) return;
        const byRef = new Map(h.brands.flatMap((b) => b.boxes.map((x) => [x.boxRef, x] as const)));
        setBoxes(
          draft.boxRefs
            .map((ref) => byRef.get(ref))
            .filter((b): b is NonNullable<typeof b> => !!b)
            .map((b) => ({
              boxRef: b.boxRef,
              productName: b.productName,
              packLabel: b.packLabel,
              boxYear: b.boxYear,
              sticksInBox: b.sticksInBox,
            })),
        );
        const looseById = new Map(h.loose.map((l) => [l.productId, l] as const));
        setLoose(
          Object.entries(draft.loose)
            .map(([pid, sticks]) => {
              const l = looseById.get(pid);
              return l ? { productId: pid, name: l.name, sticks } : null;
            })
            .filter((x): x is NonNullable<typeof x> => !!x),
        );
        setShops(s.items);
      })
      .catch((err) => live && setLoadError(errorText(err, t)));
    return () => {
      live = false;
    };
  }, [draft, t]);

  /* F2 — the chip strip is derived from the server's answer only: window
   * dates come from minDate..maxDate, closures from published
   * closedWeekdays. An answer is trusted for display while it belongs to
   * an identity that is currently authoritative — the rendered session
   * owner or the live dispatch scope (production keeps them equal; tests
   * flip the dispatch scope bare). Dispatch safety never relies on this:
   * the submit gate re-proves elig.owner === live scope before minting. */
  const eligCurrent =
    elig.status === 'ready' &&
    elig.shopId === shopId &&
    (elig.owner === ownerScope || elig.owner === moduleScope)
      ? elig.data
      : null;
  const dates = useMemo(() => {
    if (!eligCurrent) return [];
    const closed = new Set(eligCurrent.closedWeekdays);
    return eligibilityDateKeys(eligCurrent).map((key) => ({
      key,
      closed: closed.has(civilWeekday(key) ?? -1),
    }));
  }, [eligCurrent]);

  /* Submit readiness derives from the current eligible shop/date pair —
   * an answer that is loading, errored, stale, for another shop/owner, or
   * that marks the shop unavailable can never enable a new submission. */
  const eligUsable = !!eligCurrent && !eligibilityStale(eligCurrent);
  const canSubmit =
    boxes.length + loose.length > 0 &&
    !!shopId &&
    !!date &&
    eligUsable &&
    !!eligCurrent &&
    isDateEligible(date, eligCurrent) &&
    !busy;

  const submitBody = (): SubmitPickupRequestBody => ({
    items: [
      ...boxes.map((b) => ({ kind: 'BOX' as const, boxRef: b.boxRef })),
      ...loose.map((l) => ({ kind: 'LOOSE' as const, productId: l.productId, sticks: l.sticks })),
    ],
    pickupShopId: shopId!,
    preferredPickupDate: date!,
    ...(note.trim() ? { customerNote: note.trim() } : {}),
  });

  /** F1.1 — authoritative intent probe against the server-side marker.
   * 'absent' and 'received' are honest states, never proof of failure;
   * 'unknown' keeps the intent for a later probe instead of discarding it. */
  const probeIntent = async (key: string): Promise<IntentProbe> => {
    if (!key) return { kind: 'unknown' };
    try {
      const res = await portalApi.get<PickupRequestIntentStatus>(
        `/pickup-requests/intent/${encodeURIComponent(key)}`,
      );
      if (res.status === 'COMMITTED' && res.request) {
        return { kind: 'committed', requestNumber: res.request.requestNumber };
      }
      if (res.status === 'REJECTED') {
        return { kind: 'rejected', code: res.rejectCode ?? undefined };
      }
      if (res.status === 'RECEIVED') {
        const age = res.receivedAt ? Date.now() - Date.parse(res.receivedAt) : 0;
        return { kind: 'received', stale: age > RECEIVED_STALE_MS };
      }
      return { kind: 'absent' };
    } catch (err) {
      if (isPortalApiError(err) && (err.status === 401 || err.status === 403)) {
        return { kind: 'unauthorized' };
      }
      return { kind: 'unknown' };
    }
  };

  const finishCommitted = (
    requestNumber: string,
    intent: CollectIntent | null | undefined,
    owner: string | null,
  ) => {
    /* F1.3 — a stale continuation mutates nothing: if the session owner
     * changed while the response was in flight, the original owner's durable
     * handle stays for reconciliation on the next sign-in, and the new
     * owner's draft/intent is never touched. */
    if (getPortalOwnerScope() !== owner) return;
    clearIntent(owner);
    setPendingIntent(null);
    // The draft is only consumed when it produced the committed request — and
    // "draft" means the box/loose selection persisted in storage, not the
    // page-local shop/date/note fields. An edited selection (made on /humidor
    // while the request was in flight) stays so the customer can file it next.
    const d = loadDraft(owner);
    const sameSelection =
      !intent ||
      (!!intent.body &&
        intentBodyEquals(intent.body, {
          ...intent.body,
          items: [
            ...d.boxRefs.map((boxRef) => ({ kind: 'BOX' as const, boxRef })),
            ...Object.entries(d.loose).map(([productId, sticks]) => ({
              kind: 'LOOSE' as const,
              productId,
              sticks,
            })),
          ],
        }));
    if (sameSelection) clearDraft(owner);
    setDone({ requestNumber });
  };

  // Recover a pending intent on mount against the authoritative marker:
  // COMMITTED resolves to its reference; a fresh RECEIVED marker or a recent
  // attempt means the submit is probably still in flight ('processing');
  // anything unproven keeps the recovery handle ('uncertain'). Only a verdict
  // the server owns — REJECTED, or ABSENT with no dispatched attempt — leaves
  // the banner as a plain same-key resume ('pending').
  useEffect(() => {
    const intent = pendingIntent;
    if (!intent) return;
    // The probe resolves under the owner captured at mount — a session
    // switch while it is in flight must not let this continuation finish
    // (and thereby clear) under another owner (F1.3).
    const owner = getPortalOwnerScope();
    let live = true;
    probeIntent(intent.key)
      .then((probe) => {
        if (!live) return;
        if (probe.kind === 'committed') {
          finishCommitted(probe.requestNumber, intent, owner);
          return;
        }
        if (probe.kind === 'received' && !probe.stale) {
          setPhase('processing');
          return;
        }
        if (
          probe.kind === 'rejected' ||
          (probe.kind === 'absent' &&
            (!intent.lastAttemptAt ||
              Date.now() - Date.parse(intent.lastAttemptAt) > RECEIVED_STALE_MS))
        ) {
          // Server-verified terminal (or never-received): a same-key resume is
          // safe and not an uncertain outcome.
          setPhase('pending');
          return;
        }
        setPhase('uncertain');
      })
      .catch(() => {
        if (live) setPhase('uncertain');
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** An abandoned (or already-settled) intent releases the key so the edited
   * form may continue under a fresh intent. Returns true when the caller may
   * proceed with intent === null. Every mutation stays bound to `owner` —
   * if the session owner changed while the call was in flight the
   * continuation is stale and stops with PortalOwnerChangedError (F1.3). */
  const abandonIntent = async (intent: CollectIntent, owner: string | null): Promise<boolean> => {
    try {
      const res = await portalApi.post<{
        status: 'COMMITTED' | 'REJECTED';
        request?: { id: string; requestNumber: string; status: string; submittedAt: string } | null;
        rejectCode?: string | null;
      }>(
        `/pickup-requests/intent/${encodeURIComponent(intent.key)}/abandon`,
        undefined,
        intent.key,
        {
          /* The adapter's CSRF await is an async boundary here too — the
           * captured owner must still be active when the abandon actually
           * leaves, or a stale continuation would tombstone under the new
           * owner's session. Abandon deliberately has NO durable-binding
           * prerequisite: it exists to resolve durable conflicts. */
          beforeSend: () => {
            if (getPortalOwnerScope() !== owner) throw new PortalOwnerChangedError();
          },
        },
      );
      if (getPortalOwnerScope() !== owner) throw new PortalOwnerChangedError();
      if (res.status === 'COMMITTED') {
        // The in-flight original won — recover it, never replace it.
        if (res.request) {
          finishCommitted(res.request.requestNumber, intent, owner);
        } else {
          markIntentUncertain(owner, intent);
          setPhase('uncertain');
        }
        return false;
      }
      // REJECTED — the tombstone held; the replacement may proceed.
      clearIntent(owner);
      setPendingIntent(null);
      return true;
    } catch (err) {
      if (err instanceof PortalOwnerChangedError) throw err;
      if (getPortalOwnerScope() !== owner) throw new PortalOwnerChangedError();
      if (isPortalApiError(err) && err.code === 'PORTAL_INTENT_PROCESSING') {
        // The original is committing right now — replacement is not safe.
        markIntentUncertain(owner, intent);
        setPhase('processing');
        return false;
      }
      markIntentUncertain(owner, intent);
      setPhase('uncertain');
      return false;
    }
  };

  /** F2.1-B — re-check the stored intent's outcome on demand. Probe-only:
   * the intent GET is the only traffic — this action never builds a body
   * from the current form, never mints a key and never sends a pickup
   * POST, so it stays reachable while current eligibility is loading,
   * stale, unavailable or failing, or while no date is selectable. Every
   * mutation stays bound to the owner captured at click; an owner change
   * in flight stops the continuation without touching either owner's
   * recovery state (F1.3). Overlapping checks are blocked by
   * `recoveryBusy`. */
  const recheckIntent = async () => {
    const intent = pendingIntent;
    if (!intent || recoveryBusy) return;
    /* Owner captured at click (F1.3): the probe resolves server-side under
     * the caller's account+company, so a key stored under another owner
     * can only answer 'absent' — and every mutation below is bound to
     * this captured owner, rechecked once the response lands. */
    const owner = getPortalOwnerScope();
    setRecoveryBusy(true);
    try {
      const probe = await probeIntent(intent.key);
      if (getPortalOwnerScope() !== owner) return; // stale continuation — mutate nothing
      if (probe.kind === 'committed') {
        finishCommitted(probe.requestNumber, intent, owner);
        return;
      }
      if (probe.kind === 'rejected') {
        // Terminal verdict the server owns — release the handle, keep the
        // selection, and explain the next step (same as the settled path).
        clearIntent(owner);
        setPendingIntent(null);
        setError(t('collect.new.submitRejected'));
        return;
      }
      if (probe.kind === 'received' && !probe.stale) {
        setPhase('processing');
        return;
      }
      if (
        probe.kind === 'absent' &&
        (!intent.lastAttemptAt || Date.now() - Date.parse(intent.lastAttemptAt) > RECEIVED_STALE_MS)
      ) {
        // Absence alone never discards or replaces the handle (F1.1) —
        // but with no dispatched attempt it is a safe plain resume, not
        // an uncertain outcome.
        setPhase('pending');
        return;
      }
      // Stale RECEIVED, recent-attempt ABSENT, unknown, unauthorized —
      // keep the handle for a later controlled retry.
      setPhase('uncertain');
    } catch {
      setPhase('uncertain');
    } finally {
      setRecoveryBusy(false);
    }
  };

  /** F1.1/F1.3 — authoritatively resolve a prior intent that conflicts with
   * the submission being prepared: an edited body, or a durable recovery
   * record bound to a different key. Probe → recover / keep / serialized
   * tombstone; returns true only when the caller may proceed past it. All
   * storage mutations stay bound to the captured `owner`. */
  const resolvePriorIntent = async (
    prior: CollectIntent,
    owner: string | null,
  ): Promise<boolean> => {
    const probe = await probeIntent(prior.key);
    if (getPortalOwnerScope() !== owner) throw new PortalOwnerChangedError();
    if (probe.kind === 'committed') {
      finishCommitted(probe.requestNumber, prior, owner);
      return false;
    }
    if (probe.kind === 'unknown' || probe.kind === 'unauthorized') {
      markIntentUncertain(owner, prior);
      setPhase('uncertain');
      return false;
    }
    if (probe.kind === 'received' && !probe.stale) {
      setPhase('processing');
      return false;
    }
    // rejected / absent / stale-received / degraded body — the server
    // coordinates the tombstone so a late-arriving POST can never commit
    // under a key the customer already replaced.
    return abandonIntent(prior, owner);
  };

  /** Codes that MAY correspond to a settled business verdict — but are never
   * proof on their own (F1.2-B): the pre-controller ValidationPipe and the
   * exception filter emit PORTAL_VALIDATION_FAILED before the submit handler
   * runs, so the marker can still be RECEIVED or never have existed. Every
   * code here is resolved against the authoritative intent endpoint before
   * the local handle may be discarded. */
  const VERDICT_CANDIDATE_CODES = new Set([
    'PORTAL_VALIDATION_FAILED',
    'PORTAL_INSUFFICIENT_STOCK',
    'PORTAL_BOX_UNAVAILABLE',
    'PORTAL_CONTRACT_NOT_ACTIVE',
    'PORTAL_TOO_MANY_PENDING',
    'PORTAL_SHOP_UNAVAILABLE',
    // PORTAL_INTENT_SETTLED is already resolved by its dedicated branch above.
  ]);

  /** Only deterministic server rejections (which settle the marker to
   * REJECTED) may discard an intent. 429/408/edge/CSRF/timeouts/probe
   * failures reject the attempt, never the intent.
   * F1.3 — `owner` is the session owner captured when the submit began: if
   * the active owner changed anywhere across the async flow, this is a stale
   * continuation — no probes under the new session and no writes into either
   * namespace; the original owner's durable handle stays untouched. */
  const failSubmit = async (err: unknown, intent: CollectIntent | null, owner: string | null) => {
    if (isStepUpCancelled(err)) return;
    const ownerLost = () => {
      setPendingIntent(null);
      setError(t('collect.new.ownerChanged'));
    };
    if (err instanceof PortalOwnerChangedError || getPortalOwnerScope() !== owner) {
      ownerLost();
      return;
    }
    if (err instanceof PortalIntentNotDurableError) {
      // The durable recovery record stopped binding to this intent — nothing
      // may dispatch; the handle stays exactly as it was.
      setError(
        err.binding === 'conflict' ? t('collect.new.conflict') : t('collect.new.blockedStorage'),
      );
      return;
    }
    if (isPortalApiError(err) && err.code === 'PORTAL_INTENT_PROCESSING') {
      markIntentUncertain(owner, intent ?? undefined);
      setPhase('processing');
      return;
    }
    if (isPortalApiError(err) && err.code === 'PORTAL_INTENT_SETTLED') {
      // The key is already settled — the verdict is authoritative.
      const probe = intent ? await probeIntent(intent.key) : { kind: 'unknown' as const };
      if (getPortalOwnerScope() !== owner) {
        ownerLost();
        return;
      }
      if (probe.kind === 'committed') {
        finishCommitted(probe.requestNumber, intent, owner);
        return;
      }
      if (probe.kind === 'rejected') {
        clearIntent(owner);
        setPendingIntent(null);
        setError(t('collect.new.conflict'));
        return;
      }
      markIntentUncertain(owner, intent ?? undefined);
      setPhase('uncertain');
      return;
    }
    if (err && isPortalApiError(err) && err.code === 'PORTAL_IDEMPOTENCY_MISMATCH') {
      // The key is bound to a different body server-side — probe is
      // authoritative; a failed probe is an unknown outcome, never a delete.
      const probe = intent ? await probeIntent(intent.key) : { kind: 'unknown' as const };
      if (getPortalOwnerScope() !== owner) {
        ownerLost();
        return;
      }
      if (probe.kind === 'committed') {
        finishCommitted(probe.requestNumber, intent, owner);
        return;
      }
      if (probe.kind === 'rejected') {
        clearIntent(owner);
        setPendingIntent(null);
        setError(t('collect.new.conflict'));
        return;
      }
      // 'absent' here contradicts the mismatch the server just reported —
      // treat it like any other unproven outcome and keep the handle.
      markIntentUncertain(owner, intent ?? undefined);
      setPhase('uncertain');
      return;
    }
    if (!isPortalApiError(err) || err.status >= 500 || err.code === 'PORTAL_UNAVAILABLE') {
      markIntentUncertain(owner, intent ?? undefined);
      setPhase('uncertain');
      return;
    }
    if (err.status === 429 || err.status === 408) {
      // A throttle/edge rejection rejects only this attempt — the original
      // intent may still be in flight or already committed.
      markIntentUncertain(owner, intent ?? undefined);
      setPhase('uncertain');
      return;
    }
    if (
      err.code === 'PORTAL_SESSION_INVALID' ||
      err.code === 'PORTAL_UNAUTHENTICATED' ||
      err.code === 'PORTAL_STEP_UP_REQUIRED'
    ) {
      // Keep the intent: same-owner re-authentication/step-up resumes it safely.
      setError(errorText(err, t));
      return;
    }
    if (VERDICT_CANDIDATE_CODES.has(err.code)) {
      /* F1.2-B — the error code alone is never the verdict: pre-handler
       * rejections (ValidationPipe, CSRF, edge) emit the same codes while the
       * marker stays RECEIVED or never existed. Resolve the marker first;
       * only an authoritative terminal outcome may discard the handle. */
      const probe = intent ? await probeIntent(intent.key) : { kind: 'unknown' as const };
      if (getPortalOwnerScope() !== owner) {
        ownerLost();
        return;
      }
      if (probe.kind === 'committed') {
        finishCommitted(probe.requestNumber, intent, owner);
        return;
      }
      if (probe.kind === 'rejected') {
        // Server-owned terminal verdict — safe to clear the recovery handle.
        clearIntent(owner);
        setPendingIntent(null);
        setError(errorText(err, t));
        return;
      }
      // received / absent / unknown / unauthorized: the original attempt may
      // still be live — retain the handle. The attempt's own error stays
      // visible so business feedback is never a silent loop.
      markIntentUncertain(owner, intent ?? undefined);
      setPhase(probe.kind === 'received' && !probe.stale ? 'processing' : 'uncertain');
      setError(errorText(err, t));
      return;
    }
    // Anything else (edge 4xx, CSRF, unclassified) is an unknown outcome —
    // the recovery handle stays.
    markIntentUncertain(owner, intent ?? undefined);
    setPhase('uncertain');
  };

  /* Proactive step-up ("Confirm with a passkey"/code), then submit under the
   * persisted intent; withStepUp re-covers if the window lapses mid-submit.
   * F1.3 — the owner scope is captured ONCE at submit start; every async
   * boundary (the step-up ceremony, the automatic retry, the CSRF await, a
   * late response) re-verifies it before dispatch or mutation. */
  const onConfirm = async () => {
    const body = submitBody();
    setBusy(true);
    setError(null);
    setNotice(null);
    const owner = getPortalOwnerScope();
    const ownerIsCurrent = () => getPortalOwnerScope() === owner;
    let intent = loadIntent(owner);
    let replaced = false;
    try {
      if (intent && !intentBodyEquals(intent.body, body)) {
        // Edited submission: the original intent must be authoritatively
        // settled before a replacement key may exist. An empty snapshot, a
        // throttled probe or a dead handle can never delete it.
        if (!(await resolvePriorIntent(intent, owner))) return;
        intent = null;
        replaced = true;
      }
      if (!intent) {
        /* F2 — a NEW intent may only mint under current server eligibility:
         * refresh when the held answer is stale or missing, then prove the
         * chosen date is still eligible for this shop. Resumed intents skip
         * the gate on purpose — their POST may be replaying a committed
         * request, which the server decides authoritatively. Nothing is
         * minted, dispatched or reconciled away when eligibility is
         * unknown — the customer just retries when it recovers. */
        const cur = eligRef.current;
        const fresh =
          cur.status === 'ready' &&
          cur.shopId === shopId &&
          cur.owner === owner &&
          !eligibilityStale(cur.data)
            ? cur.data
            : await fetchEligibility(shopId!);
        if (!ownerIsCurrent()) throw new PortalOwnerChangedError();
        if (!fresh || !fresh.available || !date || !isDateEligible(date, fresh)) {
          setError(t('collect.new.dateUnavailable'));
          return;
        }
        intent = {
          key: newIdempotencyKey(),
          body,
          state: 'submitting',
          createdAt: new Date().toISOString(),
        };
        // Kept in memory for a same-key retry even when persistence fails —
        // the durable-binding gate below decides whether it may leave.
        saveIntent(intent, owner);
      }
      if (!intent.body) {
        // Degraded stub-only handle cannot be replayed — keep it for probing.
        markIntentUncertain(owner, intent);
        setPhase('uncertain');
        return;
      }
      /* F1.2-A — a POST may only leave under an owner-scoped recovery record
       * that would survive this tab. Missing scope (old-API window) or a
       * storage write that never landed would leave the key as module memory
       * a reload destroys — an orphaned submission. Blocking here keeps the
       * same key/body for the next try and is honest: nothing was sent yet.
       * A stub-only record still permits dispatch (probe-only recovery). */
      if (!owner) {
        setError(t('collect.new.blockedScope'));
        return;
      }
      /* F1.3 — the durable record must belong to THIS intent: same owner
       * namespace, same key, same canonical body. A record bound to a
       * different key is a conflict — resolve it through the authoritative
       * flow (probe → recover/tombstone); it is never silently overwritten
       * and never authorizes this intent. */
      let binding = persistedIntentBinding(intent, owner);
      if (binding.kind === 'conflict') {
        const durable = loadPersistedIntent(owner);
        if (!durable) {
          // Unidentifiable durable bytes — cannot be probed; block honestly
          // instead of overwriting what may be an unresolved submission.
          markIntentUncertain(owner, intent);
          setPhase('uncertain');
          setError(t('collect.new.conflict'));
          return;
        }
        if (!(await resolvePriorIntent(durable, owner))) return;
        replaced = true;
        binding = persistedIntentBinding(intent, owner);
      }
      /* A memory-only handle means an earlier write never landed — retry the
       * SAME key/body once (storage may have recovered since) before giving
       * up. The intent is never replaced just because storage is down. */
      if (binding.kind === 'none') {
        saveIntent(intent, owner);
        binding = persistedIntentBinding(intent, owner);
      }
      if (binding.kind === 'conflict') {
        markIntentUncertain(owner, intent);
        setPhase('uncertain');
        setError(t('collect.new.conflict'));
        return;
      }
      if (binding.kind === 'none') {
        setError(t('collect.new.blockedStorage'));
        return;
      }
      setStorageWeak(binding.kind !== 'match');
      setPendingIntent(intent);
      // Only announce the tombstoned replacement once dispatch is actually
      // about to happen — a blocked retry must not claim it was sent.
      if (replaced) setNotice(t('collect.new.abandoned'));
      const stable = intent as CollectIntent & { body: SubmitPickupRequestBody };
      await requireStepUp();
      /* The ceremony's session refresh may have switched owners — re-verify
       * before any dispatch attempt, including inside the automatic retry
       * and the client's own CSRF await (beforeSend). */
      if (!ownerIsCurrent()) throw new PortalOwnerChangedError();
      const stamped = touchIntentAttempt(stable, owner);
      /* F1.3 — ONE synchronous dispatch gate shared by the retry callback
       * and the adapter's beforeSend hook so the two can never drift: the
       * captured owner must still be the active session owner AND the
       * durable recovery record must still bind to this exact key/body.
       * The CSRF await inside portalApi.post is itself an async boundary —
       * beforeSend is the last application-level check before fetch. It
       * never mutates: a conflict/none aborts the attempt and leaves every
       * record untouched for the authoritative reconcile flow. */
      const assertDispatchable = () => {
        if (!ownerIsCurrent()) throw new PortalOwnerChangedError();
        const bound = persistedIntentBinding(stamped, owner);
        if (bound.kind !== 'match' && bound.kind !== 'stub') {
          throw new PortalIntentNotDurableError(bound.kind);
        }
      };
      const res = await withStepUp(() => {
        assertDispatchable();
        return portalApi.post<PickupRequestDetail & { replayed?: boolean }>(
          '/pickup-requests',
          stamped.body,
          stamped.key,
          { beforeSend: assertDispatchable },
        );
      });
      if (!ownerIsCurrent()) throw new PortalOwnerChangedError();
      finishCommitted(res.requestNumber, stamped, owner);
    } catch (err) {
      await failSubmit(err, intent ?? loadIntent(owner), owner);
    } finally {
      setBusy(false);
    }
  };

  /* R5 — device-neutral verification wording: passkey support does not
   * identify the authenticator (Face ID / fingerprint / security key), so
   * the copy says "a passkey" and falls back to the code flow wording. */
  const methodLabel = passkeySupported()
    ? t('collect.new.methodPasskey')
    : t('collect.new.methodCode');

  /* UX25-04 — searchable pickup-point choice for long shop lists. */
  const filteredShops = useMemo(() => {
    const q = shopQuery.trim().toLowerCase();
    if (!q) return shops;
    return shops.filter((s) =>
      [s.name, ...(s.addressLines ?? []), s.openingHoursText ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [shops, shopQuery]);

  /* UX25-04 — the disabled CTA must say why it cannot continue yet. */
  const blockedReason =
    canSubmit || busy
      ? null
      : boxes.length + loose.length === 0
        ? t('collect.new.needItems')
        : !shopId
          ? t('collect.new.needShop')
          : !date
            ? t('collect.new.needDate')
            : t('collect.new.dateUnavailable');
  const selectedShop = shops.find((s) => s.id === shopId) ?? null;

  if (done) {
    return (
      <div className="hp-empty" style={{ minHeight: '70dvh', justifyContent: 'center' }}>
        <SuccessTickIllustration title={t('collect.new.successTitle')} />
        <h1 className="hp-empty__sentence">
          {t('collect.new.successTitle')} {t('collect.new.successBody')}
        </h1>
        <p className="hp-caption">{t('collect.new.successRef', { number: done.requestNumber })}</p>
        <PrimaryButton onClick={() => navigate('/')} data-quality-id="portal.collect.success">
          {t('collect.new.successCta')}
        </PrimaryButton>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="hp-row" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="hp-textlink"
          onClick={() => navigate(-1)}
          aria-label={t('common.back')}
        >
          <BackIcon size={18} />
        </button>
        <h1 className="hp-display-xl">{t('collect.new.title')}</h1>
      </div>

      {loadError ? (
        <p className="hp-danger-text" role="alert">
          {loadError}
        </p>
      ) : null}

      {pendingIntent && !done ? (
        <div
          role={phase === 'uncertain' ? 'alert' : 'status'}
          data-quality-id={
            phase === 'uncertain'
              ? 'portal.collect.uncertain'
              : phase === 'processing'
                ? 'portal.collect.processing'
                : 'portal.collect.pending'
          }
        >
          <Banner style={phase === 'pending' ? 'info' : 'notice'}>
            {!pendingIntent.body ? (
              <>
                <strong>{t('collect.new.degradedTitle')}</strong> {t('collect.new.degradedBody')}
              </>
            ) : phase === 'processing' ? (
              <>
                <strong>{t('collect.new.processingTitle')}</strong>{' '}
                {t('collect.new.processingBody')}
              </>
            ) : phase === 'uncertain' ? (
              <>
                <strong>{t('collect.new.uncertainTitle')}</strong> {t('collect.new.uncertainBody')}
              </>
            ) : (
              <>
                <strong>{t('collect.new.pendingTitle')}</strong> {t('collect.new.pendingBody')}
              </>
            )}
            {storageWeak ? ` ${t('collect.new.storageWeak')}` : ''}
            {/* F2.1-B — same-page recovery is independent of the current
             * form's eligibility: probe-only, owner-bound, no overlap. */}
            <div>
              <TextLink
                data-quality-id="portal.collect.checkStatus"
                disabled={recoveryBusy}
                aria-busy={recoveryBusy || undefined}
                onClick={recheckIntent}
              >
                {recoveryBusy ? t('collect.new.checkingStatus') : t('collect.new.checkStatus')}
              </TextLink>
            </div>
          </Banner>
        </div>
      ) : null}

      {notice ? (
        <div role="status" data-quality-id="portal.collect.notice">
          <Banner style="info">{notice}</Banner>
        </div>
      ) : null}

      <section className="hp-section">
        <SectionHeader
          title={
            <span className="hp-row">
              <StepNumber n={1} label="" /> {t('collect.new.stepBoxes')}
            </span>
          }
          action={
            <TextLink onClick={() => navigate('/humidor')}>{t('collect.new.changeBoxes')}</TextLink>
          }
        />
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="hp-rows">
          {boxes.map((b) => (
            <li key={b.boxRef} className="hp-row hp-row--spread" style={{ padding: '10px 0' }}>
              <span>
                {b.productName}
                <span className="hp-listrow__sub">
                  {b.packLabel} · {b.boxYear ?? '—'} · {t('common.boxRef', { ref: b.boxRef })}
                </span>
              </span>
            </li>
          ))}
          {loose.map((l) => (
            <li key={l.productId} className="hp-row hp-row--spread" style={{ padding: '10px 0' }}>
              <span>
                {l.name}
                <span className="hp-listrow__sub">{t('common.cigars', { count: l.sticks })}</span>
              </span>
            </li>
          ))}
          {boxes.length + loose.length === 0 ? (
            <li className="hp-tertiary" style={{ padding: '10px 0' }}>
              {t('humidor.selection.empty')}
            </li>
          ) : null}
        </ul>
      </section>

      <section className="hp-section">
        <SectionHeader
          title={
            <span className="hp-row">
              <StepNumber n={2} label="" /> {t('collect.new.stepWhere')}
            </span>
          }
        />
        {shops.length > 2 ? (
          <label className="hp-field">
            <span className="hp-visually-hidden">{t('collect.new.shopSearch')}</span>
            <input
              className="hp-input"
              type="search"
              value={shopQuery}
              onChange={(e) => setShopQuery(e.target.value)}
              placeholder={t('collect.new.shopSearchPlaceholder')}
              data-quality-id="portal.collect.shopSearch"
            />
          </label>
        ) : null}
        <div className="hp-stack" role="radiogroup" aria-label={t('collect.new.stepWhere')}>
          {filteredShops.length === 0 ? (
            <p className="hp-field__hint" role="status">
              {t('collect.new.noShopMatches')}
            </p>
          ) : null}
          {filteredShops.map((shop) => (
            <ShopChoice
              key={shop.id}
              name={shop.name}
              address={shop.addressLines?.join(', ')}
              hours={shop.openingHoursText}
              selected={shopId === shop.id}
              onSelect={() => setShopId(shop.id)}
              thumbnail={
                mediaUrl(shopProfile(shop.id)?.heroAssetId) ? (
                  <img src={mediaUrl(shopProfile(shop.id)?.heroAssetId)!} alt="" />
                ) : (
                  <StorefrontIllustration title={shop.name} />
                )
              }
            />
          ))}
        </div>
      </section>

      <section className="hp-section">
        <SectionHeader
          title={
            <span className="hp-row">
              <StepNumber n={3} label="" /> {t('collect.new.stepWhen')}
            </span>
          }
        />
        {!shopId ? (
          <p className="hp-field__hint">{t('collect.new.pickShopFirst')}</p>
        ) : elig.status === 'loading' ||
          ((elig.status === 'ready' || elig.status === 'stale') && elig.shopId !== shopId) ? (
          <p className="hp-field__hint" role="status" aria-busy="true">
            {t('collect.new.datesLoading')}
          </p>
        ) : elig.status === 'error' && elig.shopId === shopId ? (
          <div>
            <p className="hp-danger-text" role="alert" data-quality-id="portal.collect.datesError">
              {elig.message}
            </p>
            <TextLink onClick={retryEligibility} data-quality-id="portal.collect.datesRetry">
              {t('collect.new.datesRetry')}
            </TextLink>
          </div>
        ) : elig.status === 'stale' && elig.shopId === shopId && !elig.exhausted ? (
          /* An automatic retry is already scheduled for this cycle — the
           * honest state is "checking", not an error the user must fix. */
          <p className="hp-field__hint" role="status" aria-busy="true">
            {t('collect.new.datesLoading')}
          </p>
        ) : elig.status === 'stale' && elig.shopId === shopId ? (
          <div>
            <p className="hp-danger-text" role="alert" data-quality-id="portal.collect.datesStale">
              {t('collect.new.datesStale')}
            </p>
            <TextLink onClick={retryEligibility} data-quality-id="portal.collect.datesRetry">
              {t('collect.new.datesRetry')}
            </TextLink>
          </div>
        ) : !eligCurrent ? null : !eligCurrent.available ? (
          <p className="hp-field__hint" role="status" data-quality-id="portal.collect.shopOff">
            {t('collect.new.shopUnavailable')}
          </p>
        ) : dates.length === 0 ? (
          <p className="hp-field__hint" role="status" data-quality-id="portal.collect.datesEmpty">
            {t('collect.new.datesEmpty')}
          </p>
        ) : (
          <div className="hp-datestrip" role="radiogroup" aria-label={t('collect.new.stepWhen')}>
            {dates.map(({ key, closed }) => (
              <DateChip
                key={key}
                date={key}
                dowLabel={civilDowLabel(key, locale)}
                dayNum={civilDayNumber(key)}
                selected={date === key}
                unavailable={closed}
                unavailableReason={t('collect.new.closedDay')}
                onSelect={() => setDate(key)}
              />
            ))}
          </div>
        )}
        <p className="hp-field__hint">{t('collect.new.whenHelp')}</p>
      </section>

      <section className="hp-section">
        <label className="hp-field">
          <span className="hp-field__label">{t('collect.new.noteLabel')}</span>
          <textarea
            className="hp-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            placeholder={t('collect.new.notePlaceholder')}
            data-quality-id="portal.collect.note"
          />
          <span className="hp-field__hint">
            {t('collect.new.noteLimit', { count: note.length })}
          </span>
        </label>
      </section>

      <p className="hp-caption" style={{ marginTop: 24 }}>
        {t('collect.new.declaration')}
      </p>

      <section className="hp-section">
        <SectionHeader
          title={
            <span className="hp-row">
              <StepNumber n={4} label="" /> {t('collect.new.reviewTitle')}
            </span>
          }
        />
        <dl className="hp-review" data-quality-id="portal.collect.review">
          <div className="hp-review__row">
            <dt>{t('collect.new.reviewItems')}</dt>
            <dd>
              {boxes.length ? (
                <>
                  <p className="hp-review__group">{t('common.boxes', { count: boxes.length })}</p>
                  <ul className="hp-plain-list">
                    {boxes.map((b) => (
                      <li key={b.boxRef}>
                        {b.productName}
                        <span className="hp-muted">
                          {' '}
                          — {b.packLabel}
                          {b.boxYear != null ? ` · ${b.boxYear}` : ''} ·{' '}
                          {t('common.cigars', { count: b.sticksInBox })} ·{' '}
                          {t('common.boxRef', { ref: b.boxRef })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {loose.length ? (
                <>
                  <p className="hp-review__group">{t('humidor.loose.title')}</p>
                  <ul className="hp-plain-list">
                    {loose.map((l) => (
                      <li key={l.productId}>
                        {l.name} · {t('common.cigars', { count: l.sticks })}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {boxes.length + loose.length === 0 ? <p className="hp-tertiary">—</p> : null}
            </dd>
          </div>
          <div className="hp-review__row">
            <dt>{t('collect.new.reviewShop')}</dt>
            <dd>{selectedShop?.name ?? '—'}</dd>
          </div>
          <div className="hp-review__row">
            <dt>{t('collect.new.reviewDate')}</dt>
            <dd>{date ? formatDate(date, locale) : '—'}</dd>
          </div>
          {note.trim() ? (
            <div className="hp-review__row">
              <dt>{t('collect.new.reviewNote')}</dt>
              <dd>{note.trim()}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}

      <div className="hp-selection-bar hp-selection-bar--flow">
        {blockedReason ? (
          <p className="hp-blocked" data-quality-id="portal.collect.blockedReason">
            {blockedReason}
          </p>
        ) : null}
        <PrimaryButton
          onClick={onConfirm}
          disabled={!canSubmit}
          loading={busy}
          data-quality-id="portal.collect.confirm"
        >
          {t('collect.new.confirm', { method: methodLabel })}
        </PrimaryButton>
      </div>
    </div>
  );
}
